import {
  Component,
  forwardRef,
  ChangeDetectorRef,
  inject,
  ChangeDetectionStrategy,
  input,
  model,
} from '@angular/core'
import {
  DEFAULT_VALUE_STEP,
  DISPLAY_MESSAGE,
  STEP,
} from '@shared/components/stepper/stepper.constants'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { Icon } from '../icon/icon'
import { TranslatePipe } from '@ngx-translate/core'

@Component({
  selector: 'bo-stepper',
  templateUrl: './stepper.html',
  styleUrls: ['./stepper.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Stepper),
      multi: true,
    },
  ],
  imports: [Icon, TranslatePipe],
})
export class Stepper implements ControlValueAccessor {
  readonly #cdr: ChangeDetectorRef = inject(ChangeDetectorRef)
  private _pendingInput: string | null = null

  readonly displayMessage = input<string>(DISPLAY_MESSAGE)
  readonly value = model<number>(DEFAULT_VALUE_STEP)
  readonly step = input<number>(STEP)
  readonly disabled = model<boolean>(false)

  onChange = (_: number) => {}
  onTouched = () => {}

  writeValue(value: number): void {
    if (value !== undefined && value !== null) {
      this.value.set(value)
      this.#cdr.markForCheck()
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled)
    this.#cdr.markForCheck()
  }

  increment() {
    if (this.disabled()) {
      return
    }
    this.value.update((value) => value + this.step())
    this.emitValue()
  }

  decrement() {
    if (this.disabled()) {
      return
    }
    this.value.set(Math.max(0, this.value() - this.step()))
    this.emitValue()
  }

  onInput(event: Event): void {
    this._pendingInput = (event.target as HTMLInputElement).value
  }

  onBlur(): void {
    this._commit()
    this.onTouched()
  }

  onEnter(event: Event): void {
    this._commit()
    ;(event.target as HTMLInputElement).blur()
  }

  private _commit(): void {
    if (this._pendingInput === null) {
      return
    }

    const parsed = parseInt(this._pendingInput, 10)
    if (!isNaN(parsed)) {
      this.value.set(Math.max(0, Math.floor(parsed)))
      this.emitValue()
    }
    this._pendingInput = null
    this.#cdr.markForCheck()
  }

  private emitValue() {
    this.onChange(this.value())
    this.onTouched()
  }
}
